import { useApolloClient } from "@apollo/client";
import { AlertStatus, Link } from "@chakra-ui/react";
import { FormikHelpers } from "formik";
import * as React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CREATE_FORM } from "src/components/graphql/mutations";
import { GET_FORM_ASSIGNMENTS_FOR_STUDENT_AND_TEMPLATE } from "src/components/graphql/queries";
import { useGetEligibleFormTemplatesForApplicant } from "src/generated/hooks/form-service/queries";
import type {
  ApplicantSummary,
  EligibleFormTemplateSummary,
} from "src/generated/hooks/form-service/requests";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { useOrganization } from "src/hooks/useOrganization";
import { useRemoteDataMutation } from "src/hooks/useRemoteDataMutation";
import { useRemoteDataQuery } from "src/hooks/useRemoteDataQuery";
import { useStartForm } from "src/scenes/parent/forms/hooks/useStartForm";
import { Forms } from "src/services/url/OrgAdmin";
import * as GQL from "src/types/graphql";
import { z } from "zod";
import { GET_ACTIVE_FORMS_BY_STUDENT_ID } from "../../students/graphql/queries";
import { useInvalidateFormList } from "./useFormsList";

/** Template from getEligibleFormTemplatesForStudent (includes can_start / cant_start_reason from API). */
type EligibleTemplateWithCanStart = EligibleFormTemplateSummary & {
  can_start: boolean;
  cant_start_reason?:
    | "already-started"
    | "not-in-audience"
    | "template-not-active"
    | "template-locked"
    | "incomplete-linked-form";
};

const FORM_RULES_HELP_URL =
  "https://help.avela.org/hc/en-us/articles/38584378786829-Form-rules-and-automation";

const CANT_START_MESSAGES: Record<
  Exclude<
    NonNullable<EligibleTemplateWithCanStart["cant_start_reason"]>,
    "incomplete-linked-form"
  >,
  string
> = {
  "already-started":
    "This student has already started a response for this form",
  "not-in-audience": "This student is not in the audience for this form",
  "template-not-active": "This form is not currently active",
  "template-locked": "This form is not accepting new submissions",
};

export const CreateFormFormSchema = z.object({
  organizationId: z.string().uuid("Organization is required"),
  enrollmentPeriodId: z.string().uuid("Enrollment period is required"),
  formTemplateId: z.string().uuid("Form is required"),
  applicantId: z.string().uuid("Student is required"),
});

export type CreateFormFormValues = z.infer<typeof CreateFormFormSchema>;

export type CreateFormSelectedApplicant = ApplicantSummary | null | undefined;

export type CreateFormSelectedTemplate =
  | EligibleFormTemplateSummary
  | null
  | undefined;

/** Minimal shape for create-form flows; dialogs may pass the full enrollment period from the API. */
export type CreateFormSelectedEnrollmentPeriod =
  | { id: string }
  | null
  | undefined;

interface UseCreateFormSubmitParams {
  selectedEnrollmentPeriod: CreateFormSelectedEnrollmentPeriod;
  selectedApplicant: CreateFormSelectedApplicant;
  selectedFormTemplate: CreateFormSelectedTemplate;
  onClose: (resetForm: () => void) => void;
}

export function useCreateFormSubmit({
  selectedEnrollmentPeriod,
  selectedApplicant,
  selectedFormTemplate,
  onClose,
}: UseCreateFormSubmitParams) {
  const enrollmentPeriodId = selectedEnrollmentPeriod?.id ?? "";
  const organization = useOrganization();
  const { startAssignedForm } = useStartForm();
  const client = useApolloClient();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const toast = useAvelaToast();
  const invalidateFormList = useInvalidateFormList();

  const {
    data: eligibleTemplatesResponse,
    isLoading: isEligibleTemplatesLoading,
  } = useGetEligibleFormTemplatesForApplicant(
    {
      query: {
        enrollmentPeriodId,
        applicantId: selectedApplicant?.id ?? "",
      },
    },
    undefined,
    {
      enabled: !!selectedEnrollmentPeriod?.id && !!selectedApplicant?.id,
    }
  );
  const eligibleTemplates = React.useMemo(
    () =>
      (eligibleTemplatesResponse?.data ?? []) as EligibleTemplateWithCanStart[],
    [eligibleTemplatesResponse?.data]
  );

  const { remoteData: getAssignedFormsRD } = useRemoteDataQuery<
    GQL.GetFormAssignmentsForStudentAndTemplate,
    GQL.GetFormAssignmentsForStudentAndTemplateVariables
  >(GET_FORM_ASSIGNMENTS_FOR_STUDENT_AND_TEMPLATE, {
    variables: {
      applicant_id: selectedApplicant?.id ?? "",
      form_template_id: selectedFormTemplate?.id ?? "",
    },
    skip: !selectedApplicant || !selectedFormTemplate,
  });

  const [createForm] = useRemoteDataMutation<
    GQL.CreateForm,
    GQL.CreateFormVariables
  >(CREATE_FORM);

  const { remoteData: remoteActiveForms } = useRemoteDataQuery<
    GQL.GetActiveFormsByStudentId,
    GQL.GetActiveFormsByStudentIdVariables
  >(GET_ACTIVE_FORMS_BY_STUDENT_ID, {
    variables: {
      id: selectedApplicant?.id || "",
    },
    skip: !selectedApplicant,
    fetchPolicy: "network-only",
  });

  const [activeFormsLookup, setActiveFormsLookup] = React.useState<
    Map<uuid, uuid>
  >(new Map());

  React.useEffect(() => {
    if (!remoteActiveForms.hasError() && remoteActiveForms.hasData()) {
      const lookup = new Map<uuid, uuid>();
      remoteActiveForms.data.form.forEach(({ id, form_template_id }) =>
        lookup.set(form_template_id, id)
      );
      setActiveFormsLookup(lookup);
    }
  }, [remoteActiveForms]);

  const [alertText, setAlertText] = React.useState<React.ReactNode | null>(
    null
  );
  const [alertStatus, setAlertStatus] = React.useState<AlertStatus>("info");

  const templateCanStartInfo = React.useMemo(() => {
    if (!selectedFormTemplate) return null;
    const template = eligibleTemplates.find(
      (t) => t.id === selectedFormTemplate?.id
    ) as EligibleTemplateWithCanStart | undefined;
    if (!template) return null;
    return {
      can_start: template.can_start,
      cant_start_reason: template.cant_start_reason,
    };
  }, [eligibleTemplates, selectedFormTemplate]);

  React.useEffect(() => {
    if (!selectedFormTemplate) {
      setAlertText(null);
      return;
    }
    if (eligibleTemplates.length > 0) {
      const template = eligibleTemplates.find(
        (t) => t.id === selectedFormTemplate?.id
      ) as EligibleTemplateWithCanStart | undefined;
      if (!template) {
        setAlertText("This student cannot start this form.");
        setAlertStatus("error");
        return;
      }
      if (template.can_start) {
        setAlertText(null);
        return;
      }
      const reason = template.cant_start_reason;
      if (reason === "incomplete-linked-form") {
        setAlertText(
          React.createElement(
            React.Fragment,
            null,
            "This student must be assigned the form before a response can be started. For more information, see how forms are ",
            React.createElement(
              Link,
              {
                href: FORM_RULES_HELP_URL,
                isExternal: true,
                color: "blue.600",
              },
              "assigned via form rules"
            )
          )
        );
      } else {
        const message = reason ? CANT_START_MESSAGES[reason] : null;
        setAlertText(message ?? "This student cannot start this form.");
      }
      setAlertStatus(reason === "already-started" ? "warning" : "error");
    } else {
      setAlertText(null);
    }
  }, [selectedFormTemplate, eligibleTemplates]);

  const showSeeFormLink =
    templateCanStartInfo?.cant_start_reason === "already-started";

  const canStartForm =
    selectedApplicant != null &&
    !isEligibleTemplatesLoading &&
    templateCanStartInfo != null &&
    templateCanStartInfo.can_start;

  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const handleSubmitForm = React.useCallback(
    async (
      values: CreateFormFormValues,
      { resetForm }: FormikHelpers<CreateFormFormValues>
    ) => {
      if (!canStartForm) {
        toast({
          title: "Cannot create form",
          description: "This form cannot be created for this student",
          status: "error",
          isClosable: true,
        });
        return;
      }
      try {
        setIsSubmitting(true);
        if (getAssignedFormsRD.hasError()) {
          console.error(
            `Could not create form: ${JSON.stringify(getAssignedFormsRD.error)}`
          );
          toast({
            title: "Failed to create form",
            status: "error",
            isClosable: true,
          });
          return;
        }

        let assignedForm = undefined;
        if (getAssignedFormsRD.hasData()) {
          assignedForm = getAssignedFormsRD.data.assigned_form.find(
            (x) => x.form_template.id === values.formTemplateId
          );
        }

        let newFormId: uuid | undefined = undefined;
        if (assignedForm) {
          newFormId = await startAssignedForm(assignedForm);
          await client.refetchQueries({
            include: [GET_ACTIVE_FORMS_BY_STUDENT_ID],
          });
        } else {
          newFormId = (
            await createForm({
              variables: {
                person_id: values.applicantId,
                form_template_id: values.formTemplateId,
              },
              refetchQueries: [GET_ACTIVE_FORMS_BY_STUDENT_ID],
            })
          ).data?.insert_form_one?.id;
        }
        invalidateFormList();

        onClose(resetForm);

        toast({
          title: "Form created",
          status: "success",
          isClosable: true,
        });

        // This is a workaround to fix the issue where the modal is not closed before the form is opened, which will
        //disable scrolling on the form detail page.
        setTimeout(() => {
          navigate(
            organization
              .map(() =>
                Forms.view({
                  organization,
                  id: newFormId || "",
                  formTemplateId: values.formTemplateId,
                  params: searchParams.toString(),
                })
              )
              .withDefault("#")
          );
        }, 100);
      } catch (error) {
        console.error(error);
        toast({
          title: "Could not create form",
          status: "error",
          isClosable: true,
        });
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      canStartForm,
      onClose,
      createForm,
      navigate,
      organization,
      searchParams,
      toast,
      startAssignedForm,
      getAssignedFormsRD,
      client,
      invalidateFormList,
    ]
  );

  const handleOnSeeFormClick = React.useCallback(
    (formTemplateId: uuid, handleClose: () => void) => {
      handleClose();
      setTimeout(() => {
        navigate(
          Forms.view({
            organization,
            id: activeFormsLookup?.get(formTemplateId) || "",
            formTemplateId: formTemplateId,
            params: searchParams.toString(),
          })
        );
      }, 100);
    },
    [navigate, organization, searchParams, activeFormsLookup]
  );

  return {
    handleSubmitForm,
    getAssignedFormsRD,
    alertText,
    alertStatus,
    showSeeFormLink: showSeeFormLink ?? false,
    handleOnSeeFormClick,
    isSubmitting,
    canStartForm,
    resetAlert: React.useCallback(() => {
      setAlertText(null);
      setAlertStatus("info");
    }, []),
  };
}
